import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Pagination,
  RiErrorWarningFill,
  Spinner,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import { FC, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useApiGetSuspenseRequest } from '@/api/rest/hooks/useApiGetSuspenseRequest';
import { useGetDraftPlot } from '@/api/rest/resources/plot';
import {
  DraftPlot,
  GET_DRAFT_PLOT_PATH,
  GetDraftPlotErrorDto,
  GetDraftPlotSuccessDto,
} from '@/api/rest/resources/types/plot';
import { PlotTypeIcon } from '@/components';
import { PlotThumbnail } from '@/components/MapThumbnail/MapThumbnail';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useSetOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { PLOTS_FETCH_LIMIT } from '@/pages/shared/hooks/usePlotsForOrganization';
import { paths } from '@/routing';
import { buildPath } from '@/utils/buildPath';
import { getDraftPlotErrorTitleKey, squareMetersToHectares } from '@/utils/plot';

const DRAFT_PLOTS_PAGE_SIZE = 10;

// Used for the "no plots" empty-state check without waiting on the accordion's own suspense fetch
export const useDraftPlotsForOrganization = (organizationId?: string): DraftPlot[] => {
  const { data } = useGetDraftPlot(
    { queryParams: { organization: organizationId, all_org_drafts: true, limit: PLOTS_FETCH_LIMIT } },
    { enabled: !!organizationId },
  );

  return data?.results ?? [];
};

const DraftPlotRow: FC<{ draftPlot: DraftPlot; organizationId: string }> = ({ draftPlot, organizationId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setOrganizationId = useSetOrganizationId();

  const hasErrors = draftPlot.errors.length > 0;
  const areaInHectares = getDisplayNumber(squareMetersToHectares(draftPlot.area), window.navigator.language);

  const onRowClick = () => {
    setOrganizationId(organizationId);
    navigate(buildPath(paths.staff.plotUpload), { state: { draftPlotId: draftPlot.id } });
  };

  return (
    <TableRow data-testid='draft-plot-row' onClick={onRowClick} className='hover:cursor-pointer'>
      <TableCell>
        <Stack direction='row' centerMain spacing={3}>
          {hasErrors ? (
            <span className='flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-bg-light-grey'>
              <RiErrorWarningFill size={20} className='text-error' data-testid='draft-plot-alert-icon' />
            </span>
          ) : (
            <PlotThumbnail plot={draftPlot} className='h-12 w-12 shrink-0 rounded-lg' />
          )}
          <span className='max-w-[150px] truncate'>{draftPlot.name}</span>
        </Stack>
      </TableCell>
      <TableCell>
        <PlotTypeIcon type={draftPlot.land_type} size={24} />
      </TableCell>
      <TableCell className='whitespace-nowrap'>{`${areaInHectares} ha`}</TableCell>
      <TableCell>
        {hasErrors ? (
          <Tooltip>
            <TooltipTrigger>
              <span className='text-error typography-body2' data-testid='draft-plot-invalid-label'>
                {t('shared.plots.draftPlots.invalid')}
              </span>
            </TooltipTrigger>
            <TooltipContent sideOffset={5} side='top'>
              {draftPlot.errors.map((error) => t(getDraftPlotErrorTitleKey(error))).join(', ')}
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className='text-text-secondary typography-body2'>{t('shared.plots.draftPlots.inProgress')}</span>
        )}
      </TableCell>
    </TableRow>
  );
};

const DraftPlotsAccordionContent: FC<{ organizationId: string }> = ({ organizationId }) => {
  const { t } = useTranslation();
  const [draftPage, setDraftPage] = useState(1);

  const queryParams = { organization: organizationId, all_org_drafts: true, limit: PLOTS_FETCH_LIMIT };
  const { data } = useApiGetSuspenseRequest<
    undefined,
    typeof queryParams,
    GetDraftPlotSuccessDto,
    GetDraftPlotErrorDto
  >(['land_steward', 'draftplot', queryParams], GET_DRAFT_PLOT_PATH, { queryParams });

  const draftPlots = data.results;

  if (draftPlots.length === 0) {
    return null;
  }

  const startIndex = (draftPage - 1) * DRAFT_PLOTS_PAGE_SIZE;
  const pageDraftPlots = draftPlots.slice(startIndex, startIndex + DRAFT_PLOTS_PAGE_SIZE);

  return (
    <Accordion type='single' collapsible>
      <AccordionItem value='draft-plots' className='rounded-2xl border border-neutral-black-12'>
        <AccordionTrigger className='px-4 typography-overline' data-testid='draft-plots-accordion-trigger'>
          {t('shared.plots.draftPlots.sectionTitle', { count: draftPlots.length })}
        </AccordionTrigger>
        <AccordionContent>
          <Table data-testid='draft-plots-table'>
            <TableHeader>
              <TableRow>
                <TableHead>{t('shared.plots.tableHeader.name')}</TableHead>
                <TableHead>{t('shared.plots.tableHeader.type')}</TableHead>
                <TableHead>{t('shared.plots.tableHeader.size')}</TableHead>
                <TableHead>{t('shared.plots.draftPlots.statusHeader')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageDraftPlots.map((draftPlot) => (
                <DraftPlotRow key={draftPlot.id} draftPlot={draftPlot} organizationId={organizationId} />
              ))}
            </TableBody>
          </Table>
          {draftPlots.length > DRAFT_PLOTS_PAGE_SIZE && (
            <Stack className='py-4'>
              <Pagination
                className='typography-overline text-text-secondary mx-auto'
                currentPage={draftPage}
                totalCount={draftPlots.length}
                pageSize={DRAFT_PLOTS_PAGE_SIZE}
                onPageChange={setDraftPage}
                data-testid='draft-plots-pagination'
              />
            </Stack>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

const DraftPlotsAccordionSkeleton: FC = () => (
  <Stack center className='h-16 rounded-2xl border border-neutral-black-12'>
    <Spinner size={20} />
  </Stack>
);

export type DraftPlotsSectionProps = {
  organizationId: string;
};

// Self-contained, collapsible (closed by default) draft plots table, separate from the main plots table
export const DraftPlotsSection: FC<DraftPlotsSectionProps> = ({ organizationId }) => (
  <Suspense fallback={<DraftPlotsAccordionSkeleton />}>
    <DraftPlotsAccordionContent organizationId={organizationId} />
  </Suspense>
);
